import classNames from 'classnames';
import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { type CategorizedEventSpotChecks } from 'components/SpotCheckPanel/hooks/useAiNotifcations';
import { type CategorizedSpotChecks } from 'components/SpotCheckPanel/hooks/useSpotChecks';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { useSelector } from 'stores/store';
import { useFeatureStandard } from 'utils/EventStandardCache/eventStandardCache';
import { type SpotCheckView } from '../SpotCheckMainContent';
import SpotCheckTable from './SpotCheckTable/SpotCheckTable';
import SpotCheckTemplates from './SpotCheckTemplates/SpotCheckTemplates';
import SpotCheckTemplatesSelection from './SpotCheckTemplatesSelection/SpotCheckTemplatesSelection';
import styles from './SpotCheckTemplatesView.module.sass';
interface SpotCheckTemplatesViewProps {
  setView: React.Dispatch<React.SetStateAction<SpotCheckView>>;
  categorizedSpotChecks: CategorizedSpotChecks | undefined;
  categorizedEventSpotChecks: CategorizedEventSpotChecks;
  setSpotCheck: React.Dispatch<React.SetStateAction<ScheduledActivityEntity | undefined>>;
  setEventSpotCheck: React.Dispatch<React.SetStateAction<AiNotification | undefined>>;
  selectedCategoryKey: string | null;
  setSelectedCategoryKey: (key: string | null) => void;
  selectedSpotCheckCategoryKey: SpotCheckCategoryKey | null;
  setSelectedSpotCheckCategoryKey: (key: SpotCheckCategoryKey | null) => void;
  spotCheck: ScheduledActivityEntity | undefined;
  eventSpotCheck: AiNotification | undefined;
  spotCheckSearchPrompt: string;
  setSpotCheckSearchPrompt: React.Dispatch<React.SetStateAction<string>>;
  eventSpotCheckSearchPrompt: string;
  setEventSpotCheckSearchPrompt: React.Dispatch<React.SetStateAction<string>>;
  setFormMode: React.Dispatch<React.SetStateAction<'create' | 'edit'>>;
  setIsExistingSpotCheck: React.Dispatch<React.SetStateAction<boolean>>;
}

const spotCheckCategoryKeysTuple = ['active', 'archived'] as const;

export type SpotCheckCategoryKey = (typeof spotCheckCategoryKeysTuple)[number];

const spotCheckCategoryKeys = [...spotCheckCategoryKeysTuple] as SpotCheckCategoryKey[];

export default function SpotCheckTemplatesView(props: SpotCheckTemplatesViewProps) {
  const {
    setView,
    categorizedSpotChecks,
    categorizedEventSpotChecks,
    setSpotCheck,
    setEventSpotCheck,
    selectedCategoryKey,
    setSelectedCategoryKey,
    selectedSpotCheckCategoryKey,
    setSelectedSpotCheckCategoryKey,
    spotCheck,
    eventSpotCheck,
    spotCheckSearchPrompt,
    setSpotCheckSearchPrompt,
    eventSpotCheckSearchPrompt,
    setEventSpotCheckSearchPrompt,
    setFormMode,
    setIsExistingSpotCheck,
  } = props;
  const locale = useLocale();
  const featureStandard = useFeatureStandard();

  const spotCheckTemplateCategories = featureStandard?.spotCheckTemplateCategories;

  const language = locale.locale;

  const spotCheckCategoryMap = spotCheckCategoryKeys.reduce<
    Record<SpotCheckCategoryKey, { name: string }>
  >(
    (acc, key) => {
      acc[key] = { name: key };
      return acc;
    },
    {} as Record<SpotCheckCategoryKey, { name: string }>,
  );

  const spotCheckMode = useSelector((state) => state.filter.spotCheckMode);
  const isEventSpotCheck = spotCheckMode === 'event';

  const templates = isEventSpotCheck
    ? (featureStandard?.eventSpotCheckTemplates ?? [])
    : (featureStandard?.spotCheckTemplates ?? []);
  const activeSpotChecks = categorizedSpotChecks?.['active'] ?? [];
  const archivedSpotChecks = categorizedSpotChecks?.['archived'] ?? [];
  const activeEventSpotChecks = categorizedEventSpotChecks?.['active'] ?? [];
  const archivedEventSpotChecks = categorizedEventSpotChecks?.['archived'] ?? [];

  const templatesByCategory = templates.filter(
    (template) =>
      selectedCategoryKey === 'all' ||
      template.metadata?.templateCategoryKey === selectedCategoryKey,
  );
  const updateSelectedCategoryKey = (key: string | null) => {
    setSelectedCategoryKey(key);
    if (key !== null) {
      setSelectedSpotCheckCategoryKey(null);
    }
  };

  const updateSelectedSpotCheckCategoryKey = (key: SpotCheckCategoryKey | null) => {
    setSelectedSpotCheckCategoryKey(key);
    if (key !== null) {
      setSelectedCategoryKey(null);
    }
  };

  const isCardView = !!selectedCategoryKey;

  const spotChecks =
    selectedSpotCheckCategoryKey === 'active' ? activeSpotChecks : archivedSpotChecks;
  const eventSpotChecks =
    selectedSpotCheckCategoryKey === 'active' ? activeEventSpotChecks : archivedEventSpotChecks;

  const templateTitle =
    spotCheckTemplateCategories?.[selectedCategoryKey ?? '']?.i18n[language].name ??
    locale.general.all;

  const spotCheckTitle = selectedSpotCheckCategoryKey
    ? spotCheckCategoryMap[selectedSpotCheckCategoryKey]?.name
    : '';

  return (
    <div className={styles.spotCheckTemplatesView}>
      <div className={styles.spotCheckTemplatesSidePanel}>
        <SpotCheckTemplatesSelection
          spotCheckTemplateCategories={spotCheckTemplateCategories}
          spotCheckCategoryKeys={spotCheckCategoryKeys}
          updateSelectedCategoryKey={updateSelectedCategoryKey}
          updateSelectedSpotCheckCategoryKey={updateSelectedSpotCheckCategoryKey}
          templates={templates}
          spotCheckCategoryMap={spotCheckCategoryMap}
          selectedCategoryKey={selectedCategoryKey}
          selectedSpotCheckCategoryKey={selectedSpotCheckCategoryKey}
        />
      </div>
      <div
        className={classNames(styles.spotCheckTemplatesContent, { [styles.cardView]: isCardView })}
      >
        {isCardView && (
          <SpotCheckTemplates
            setView={setView}
            title={templateTitle}
            templates={templatesByCategory}
            setSpotCheck={setSpotCheck}
            setEventSpotCheck={setEventSpotCheck}
            spotCheckSearchPrompt={spotCheckSearchPrompt}
            setSpotCheckSearchPrompt={setSpotCheckSearchPrompt}
            eventSpotCheckSearchPrompt={eventSpotCheckSearchPrompt}
            setEventSpotCheckSearchPrompt={setEventSpotCheckSearchPrompt}
            selectedCategoryKey={selectedCategoryKey}
            setFormMode={setFormMode}
            setIsExistingSpotCheck={setIsExistingSpotCheck}
          />
        )}
        {!isCardView && (
          <SpotCheckTable
            setView={setView}
            setSpotCheck={setSpotCheck}
            setEventSpotCheck={setEventSpotCheck}
            spotChecks={spotChecks}
            eventSpotChecks={eventSpotChecks}
            title={spotCheckTitle}
            selectedSpotCheckCategoryKey={selectedSpotCheckCategoryKey}
            spotCheck={spotCheck}
            eventSpotCheck={eventSpotCheck}
            setFormMode={setFormMode}
            setIsExistingSpotCheck={setIsExistingSpotCheck}
          />
        )}
      </div>
    </div>
  );
}
